/**
 * Custom hook for managing form responses data fetching and state
 */

import { useState, useEffect, useCallback } from 'react';
import {
    getFormResponsesByOwner,
    getPropertyFormResponses,
} from '../../../firebase/formResponse.service';
import useAppStore from '../../../store/appStore';

/**
 * Hook for fetching and managing form responses
 * @param {string} ownerId - The owner/user ID
 * @param {Object} options - Configuration options
 * @param {string} [options.propertyId] - Filter by property ID
 * @param {'owner'|'property'} [options.type='owner'] - Fetch type
 * @returns {Object} - { responses, loading, error, refetch }
 */
export const useFormResponses = (ownerId, options = {}) => {
    const { propertyId, type = 'owner' } = options;

    // For owner-level, read from Zustand store (populated by realtimeSubscriptions)
    const storeResponses = useAppStore((s) => s.ctFormResponses);

    const [responses, setResponses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!ownerId) {
            setResponses([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        if (type === 'property' && propertyId) {
            // For single-fetch property responses (not real-time)
            const fetchPropertyResponses = async () => {
                try {
                    const data = await getPropertyFormResponses(ownerId, propertyId);
                    setResponses(data);
                    setLoading(false);
                } catch (err) {
                    console.error('[useFormResponses] Error fetching property responses:', err);
                    setError(err.message || 'Failed to fetch responses');
                    setLoading(false);
                }
            };

            fetchPropertyResponses();
        } else {
            // For owner-level, use Zustand store data
            if (storeResponses !== null) {
                setResponses(storeResponses);
                setLoading(false);
            }
            // If store is null, data hasn't loaded yet — will update when storeResponses changes
        }
    }, [ownerId, propertyId, type, storeResponses]);

    const refetch = useCallback(async () => {
        if (!ownerId) return;

        try {
            setLoading(true);
            setError(null);

            if (type === 'property' && propertyId) {
                const data = await getPropertyFormResponses(ownerId, propertyId);
                setResponses(data);
            } else {
                const data = await getFormResponsesByOwner(ownerId);
                setResponses(data);
            }
        } catch (err) {
            console.error('[useFormResponses] Error refetching:', err);
            setError(err.message || 'Failed to refetch responses');
        } finally {
            setLoading(false);
        }
    }, [ownerId, propertyId, type]);

    return {
        responses,
        loading,
        error,
        refetch,
    };
};

/**
 * Hook for managing form response detail view state
 * @param {Object} initialResponse - Initial response to display
 * @returns {Object} - { response, isOpen, open, close, setResponse }
 */
export const useFormResponseDetail = (initialResponse = null) => {
    const [response, setResponse] = useState(initialResponse);
    const [isOpen, setIsOpen] = useState(!!initialResponse);

    const open = useCallback((responseData) => {
        setResponse(responseData);
        setIsOpen(true);
    }, []);

    const close = useCallback(() => {
        setIsOpen(false);
        // Optional: clear response after animation completes
        setTimeout(() => setResponse(null), 300);
    }, []);

    return {
        response,
        isOpen,
        open,
        close,
        setResponse,
    };
};

/**
 * Hook for table pagination and sorting
 * @param {Array} items - Items to paginate
 * @param {Object} options - Configuration
 * @param {number} [options.pageSize=10] - Items per page
 * @returns {Object} - { paginatedItems, page, pageSize, totalPages, goToPage, nextPage, prevPage, setSortBy, sortBy }
 */
export const useTablePagination = (items = [], options = {}) => {
    const { pageSize = 10 } = options;

    const [page, setPage] = useState(0);
    const [sortBy, setSortBy] = useState(null);
    const [sortOrder, setSortOrder] = useState('desc');

    // Sort items if sortBy is set
    const sortedItems = sortBy
        ? [...items].sort((a, b) => {
            let aVal = a[sortBy];
            let bVal = b[sortBy];

            // Handle dates
            if (aVal?.toDate) aVal = aVal.toDate().getTime();
            if (bVal?.toDate) bVal = bVal.toDate().getTime();

            if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        })
        : items;

    const totalPages = Math.ceil(sortedItems.length / pageSize);
    const startIdx = page * pageSize;
    const paginatedItems = sortedItems.slice(startIdx, startIdx + pageSize);

    const goToPage = useCallback((pageNum) => {
        setPage(Math.max(0, Math.min(pageNum, totalPages - 1)));
    }, [totalPages]);

    const nextPage = useCallback(() => {
        goToPage(page + 1);
    }, [page, goToPage]);

    const prevPage = useCallback(() => {
        goToPage(page - 1);
    }, [page, goToPage]);

    const handleSort = useCallback((column) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('desc');
        }
        setPage(0); // Reset to first page on sort
    }, [sortBy, sortOrder]);

    return {
        paginatedItems,
        page,
        pageSize,
        totalPages,
        totalItems: sortedItems.length,
        goToPage,
        nextPage,
        prevPage,
        setSortBy: handleSort,
        sortBy,
        sortOrder,
    };
};
